import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins,
  faHistory,
  faGift,
  faUserPlus,
  faShoppingCart,
  faCopy,
  faCheck,
  faQuestionCircle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';

const PointsDashboard = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);


  useEffect(() => {
    const fetchPointsData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('AficFreshtoken');
        
        const response = await axios.get('https://afric-fresh-backend.onrender.com/user/points', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setUserData(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching points data:', err);
        setError(err.response?.data?.error || 'Failed to load points data');
        setLoading(false);
      }
    };

    fetchPointsData();
  }, []);

  const copyToClipboard = () => {
    if (!userData?.referralCode) return;
    
    navigator.clipboard.writeText(userData.referralCode);
    setCopied(true);
    toast.success('Referral code copied to clipboard!');
    setTimeout(() => setCopied(false), 5000);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-green-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md text-center">
          <h2 className="text-xl font-semibold text-red-500 mb-4">Error Loading Points</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">No Points Data Found</h2>
          <p className="text-gray-600">You don't have any points history yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
          <FontAwesomeIcon icon={faCoins} className="text-yellow-500 mr-3" />
          My Rewards & Points
        </h1>

        {/* Points Summary Card */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-xl shadow-lg p-6 text-white mb-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h2 className="text-xl font-semibold mb-2">Your Current Points</h2>
              <p className="text-4xl font-bold">{userData.points} pts</p>
              <p className="mt-2 opacity-90">
                ≈ ${(userData.points / 100).toFixed(2)} redeemable credit
              </p>
            </div>
            <div className="bg-white bg-opacity-20 p-4 rounded-lg">
              <h3 className="font-medium mb-2">How to earn more?</h3>
              <ul className="text-sm space-y-1">
                <li className="flex items-center">
                  <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                  Refer friends (10 pts each)
                </li>
                <li className="flex items-center">
                  <FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
                  Make purchases (5 pts each)
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Referral Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-green-100">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FontAwesomeIcon icon={faUserPlus} className="text-green-500 mr-3" />
              Your Referral Code
            </h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Share this code with friends and earn 10 points when they make their first purchase over $50
              </p>
              <div className="flex items-center">
                <div className="bg-gray-100 px-4 py-3 rounded-lg font-mono text-lg font-bold text-gray-800 flex-grow">
                  {userData.referralCode}
                </div>
                <button
                  onClick={copyToClipboard}
                  className="ml-3 bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg transition-colors duration-200"
                  aria-label="Copy referral code"
                  disabled={!userData.referralCode}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </button>
              </div>
              {copied && (
                <p className="text-green-500 text-sm mt-2 text-center">Copied to clipboard!</p>
              )}
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-medium text-green-800 mb-2">Your Referral Stats</h3>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-2xl font-bold text-green-600">{userData.referralCount}</p>
                  <p className="text-xs text-gray-600">Friends referred</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{userData.referralCount * 10}</p>
                  <p className="text-xs text-gray-600">Points earned</p>
                </div>
              </div>
            </div>
          </div>

          {/* How Points Work Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border border-blue-100 lg:col-span-2">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FontAwesomeIcon icon={faQuestionCircle} className="text-blue-500 mr-3" />
              How Our Points System Works
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-800 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faGift} className="mr-2" />
                  Earning Points
                </h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0">1</span>
                    <span><strong>20 points</strong> - Just for signing up!</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0">2</span>
                    <span><strong>10 points</strong> - For each friend who signs up with your code <em>and</em> makes their first purchase of $50+</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0">3</span>
                    <span><strong>5 points</strong> - For every purchase you make (any amount)</span>
                  </li>
                </ul>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-medium text-purple-800 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faCoins} className="mr-2" />
                  Redeeming Points
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="bg-purple-100 text-purple-800 rounded-full w-5 h-5 flex items-center justify-center mr-2 flex-shrink-0">$</span>
                    <span><strong>100 points = $1</strong> in store credit</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-4">
                    <p>Points are automatically applied as discounts during checkout.</p>
                    <p className="mt-2">Example: 500 points = $5 discount on your order.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Points History */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <FontAwesomeIcon icon={faHistory} className="text-gray-500 mr-3" />
            Points History
          </h2>
          {userData.pointHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Points
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userData.pointHistory.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(item.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        +{item.amount} pts
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {item.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No point history yet. Start earning points today!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointsDashboard;